import React from 'react';

/**
 * Card Component - Consistent card styling with brand colors
 * Supports both light and dark themes
 */
export function Card({ 
  children, 
  className = '',
  variant = 'default', // 'default', 'highlighted', 'bordered', 'dark', 'dark-highlighted', 'dark-bordered', 'glass'
  onClick,
  padding = 'normal', // 'none', 'sm', 'normal', 'lg'
  ...props 
}) {
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    normal: 'p-6',
    lg: 'p-8',
  };

  const baseClasses = `rounded-2xl ${paddingClasses[padding]} transition-all duration-200`;
  
  const variantClasses = {
    // Light theme variants
    default: 'bg-white border border-gray-200 shadow-sm hover:shadow-md',
    highlighted: 'bg-zapt-purple/10 border border-zapt-purple/30 shadow-md',
    bordered: 'bg-white border-2 border-zapt-purple/50 shadow-lg',
    
    // Dark theme variants
    dark: 'bg-white/10 backdrop-blur-md border border-white/20 text-white shadow-xl',
    'dark-highlighted': 'bg-zapt-purple/20 backdrop-blur-md border border-zapt-purple/40 text-white shadow-xl shadow-zapt-purple/20',
    'dark-bordered': 'bg-white/10 backdrop-blur-md border-2 border-zapt-yellow/50 text-white shadow-xl shadow-zapt-yellow/10',
    'dark-solid': 'bg-zapt-navy/80 backdrop-blur-sm border border-white/10 text-white shadow-xl',
    
    // Glass effect (works on both)
    glass: 'bg-white/5 backdrop-blur-lg border border-white/10 text-white shadow-2xl',
  };

  const Component = onClick ? 'button' : 'div';
  const interactiveClasses = onClick ? 'cursor-pointer hover:scale-[1.01] active:scale-[0.99]' : '';

  return (
    <Component
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${interactiveClasses}
        ${className}
      `}
      onClick={onClick}
      {...props}
    >
      {children}
    </Component>
  );
}

/**
 * Card Header - For card titles and headers
 */
export function CardHeader({ children, className = '', dark = false }) {
  return (
    <div className={`mb-4 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Card Title - Consistent heading style
 */
export function CardTitle({ children, className = '', dark = false }) {
  const colorClass = dark ? 'text-white' : 'text-gray-900';
  return (
    <h3 className={`text-xl font-semibold ${colorClass} mb-2 ${className}`}>
      {children}
    </h3>
  );
}

/**
 * Card Description - Subtitle or description text
 */
export function CardDescription({ children, className = '', dark = false }) {
  const colorClass = dark ? 'text-gray-300' : 'text-gray-600';
  return (
    <p className={`text-sm ${colorClass} ${className}`}>
      {children}
    </p>
  );
}

/**
 * Card Content - Main card content area
 */
export function CardContent({ children, className = '' }) {
  return (
    <div className={className}>
      {children}
    </div>
  );
}

/**
 * Card Footer - For card actions/footers
 */
export function CardFooter({ children, className = '', dark = false }) {
  const borderClass = dark ? 'border-white/10' : 'border-gray-200';
  return (
    <div className={`mt-4 pt-4 border-t ${borderClass} ${className}`}>
      {children}
    </div>
  );
}

/**
 * Stat Card - For displaying statistics/metrics
 */
export function StatCard({ 
  label, 
  value, 
  icon, 
  trend, 
  trendUp,
  dark = false,
  className = '' 
}) {
  return (
    <Card variant={dark ? 'dark' : 'default'} className={className}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm font-medium ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
            {label}
          </p>
          <p className={`text-2xl font-bold mt-1 ${dark ? 'text-white' : 'text-gray-900'}`}>
            {value}
          </p>
          {trend && (
            <p className={`text-sm mt-1 ${trendUp ? 'text-green-400' : 'text-red-400'}`}>
              {trendUp ? '↑' : '↓'} {trend}
            </p>
          )}
        </div>
        {icon && (
          <div className={`text-3xl ${dark ? 'opacity-80' : ''}`}>
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Feature Card - For highlighting features or benefits
 */
export function FeatureCard({ 
  icon, 
  title, 
  description, 
  dark = false,
  className = '' 
}) {
  return (
    <Card variant={dark ? 'dark' : 'default'} className={className}>
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
          dark 
            ? 'bg-zapt-purple/30 border border-zapt-purple/50' 
            : 'bg-zapt-purple/10 border border-zapt-purple/20'
        }`}>
          {icon}
        </div>
        <div>
          <h4 className={`font-semibold ${dark ? 'text-white' : 'text-gray-900'}`}>
            {title}
          </h4>
          <p className={`text-sm mt-1 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
            {description}
          </p>
        </div>
      </div>
    </Card>
  );
}

